import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, ArrowLeft, ArrowRight, UserCheck, Radio,
  Dog, GraduationCap, Eye, Radar, Building2
} from "lucide-react";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import { CmsImageSlideshow as CmsImage } from "../lib/images";
import { CmsText } from "../lib/texts";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { waProps, waLabel } from "../lib/whatsapp";

/**
 * Dedicated editorial page for the "Seguridad Privada" (Private Security) service.
 * Verbatim brochure text in ES + faithful EN/PT translations.
 * Four client-supplied photos (CCTV operator, K9 unit, radio guard, training).
 */
export default function SeguridadPrivada() {
  const { pick } = useI18n();
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data?.data || null)).catch(() => {});
    api.post("/track/visit", { path: "/servicios/seguridad-privada" }).catch(() => {});
  }, []);

  // Paragraph 1 — Trajes a la medida
  const P1_ES = "En Xega, nos tomamos el tiempo necesario para analizar, evaluar y hacer un traje a la medida de lo que usted necesita. Cada vez que asignamos un guardia de seguridad a un puesto, nos aseguramos de que su personalidad, presentación, capacitación y habilidades se ajusten al rol. Esto garantiza un mejor desempeño y una mayor satisfacción del empleado, lo que a su vez se traduce en una mayor satisfacción del cliente.";
  const P1_EN = "At Xega we take the time to analyze, evaluate and design a tailor-made solution for what you truly need. Every time we assign a security guard to a post, we make sure that their personality, presentation, training and skills match the role. This guarantees higher performance, greater employee satisfaction and — as a direct consequence — a much more satisfied client.";
  const P1_PT = "Na Xega tomamos o tempo necessário para analisar, avaliar e criar uma solução sob medida para o que você realmente precisa. Cada vez que designamos um guarda de segurança para um posto, garantimos que sua personalidade, apresentação, capacitação e habilidades se ajustem ao papel. Isso assegura melhor desempenho, maior satisfação do funcionário e, como consequência direta, um cliente muito mais satisfeito.";

  // Paragraph 2 — Comunicación transnacional
  const P2_ES = "Somos la única empresa de seguridad transnacional que ofrece protocolos de servicio al cliente personalizados, ya que contamos con una estrecha comunicación transparente en todos los niveles — desde los guardias de seguridad en campo hasta la alta dirección. Nuestras relaciones con los clientes son exitosas porque esta comunicación nos permite el mejor entendimiento y la resolución inmediata de problemas, sin protocolos burocráticos que obstaculicen la solución.";
  const P2_EN = "We are the only transnational security company offering fully personalized customer-service protocols, because we operate with a close and transparent communication across every level — from the field guards to top management. Our client relationships are successful precisely because this communication delivers a better understanding and an immediate resolution of any issue, with no bureaucratic protocols in the way.";
  const P2_PT = "Somos a única empresa de segurança transnacional que oferece protocolos de atendimento personalizados, pois contamos com uma comunicação estreita e transparente em todos os níveis — desde os guardas em campo até a alta direção. Nossas relações com os clientes são bem-sucedidas justamente porque essa comunicação garante o melhor entendimento e a resolução imediata dos problemas, sem protocolos burocráticos no caminho.";

  // Paragraph 3 — Centro de monitoreo 360
  const P3_ES = "En XEGA contamos con uno de los centros de monitoreo más avanzados de América Latina, desde donde monitoreamos a nuestros guardias en tiempo real durante toda la jornada laboral. Somos la única empresa con una sala experta en manejo de crisis, lista para anexarse de forma electrónica y volcar su tecnología en el lugar físico de la emergencia — logrando una efectividad 360, involucrando a las autoridades con tecnología de audio, video y trackeo continuo en tiempo real de la situación de riesgo.";
  const P3_EN = "At XEGA we operate one of the most advanced monitoring centers in Latin America, from which we watch our guards in real time throughout their entire shift. We are the only company with an expert crisis-management room, ready to attach electronically and deploy its technology at the physical location of the emergency — achieving 360° effectiveness, engaging the authorities with continuous audio, video and real-time tracking of the risk scenario.";
  const P3_PT = "Na XEGA operamos um dos centros de monitoramento mais avançados da América Latina, de onde acompanhamos nossos guardas em tempo real durante toda a jornada de trabalho. Somos a única empresa com uma sala especializada em gestão de crises, pronta para se conectar eletronicamente e levar sua tecnologia até o local físico da emergência — alcançando uma efetividade 360°, envolvendo as autoridades com áudio, vídeo e rastreamento contínuo em tempo real da situação de risco.";

  const CLOSER_ES = "Xega no solamente es un guardia… es una corporación multinacional con la más avanzada tecnología respaldando a un guardia listo para operar en una reacción inmediata.";
  const CLOSER_EN = "Xega is not just a guard… it is a multinational corporation with the most advanced technology backing a guard who is ready to operate in an immediate response.";
  const CLOSER_PT = "A Xega não é apenas um guarda… é uma corporação multinacional com a mais avançada tecnologia por trás de um guarda pronto para operar em uma reação imediata.";

  const PILLARS = [
    { Icon: UserCheck, t: pick("Traje a la medida", "Tailor-made scheme", "Solução sob medida"),
      d: pick("Analizamos su operación y diseñamos el esquema exacto — perfil, presentación y habilidades del guardia.", "We analyze your operation and design the exact scheme — profile, presentation and skills of the guard.", "Analisamos sua operação e desenhamos o esquema exato — perfil, apresentação e habilidades do guarda.") },
    { Icon: Radio, t: pick("Comunicación transnacional", "Transnational communication", "Comunicação transnacional"),
      d: pick("Canales directos entre guardia, supervisión y alta dirección — sin protocolos burocráticos que frenen la solución.", "Direct channels between guard, supervision and top management — with no bureaucratic protocols slowing the response.", "Canais diretos entre guarda, supervisão e alta direção — sem protocolos burocráticos travando a solução.") },
    { Icon: Eye, t: pick("Monitoreo en tiempo real", "Real-time monitoring", "Monitoramento em tempo real"),
      d: pick("Vigilancia continua de cada guardia durante toda su jornada laboral desde el centro de control.", "Continuous surveillance of every guard throughout their shift from the control center.", "Vigilância contínua de cada guarda durante toda a jornada, a partir da central de controle.") },
    { Icon: Radar, t: pick("Sala experta en crisis", "Expert crisis room", "Sala especialista em crises"),
      d: pick("Se anexa electrónicamente al evento — audio, video y trackeo continuo en el lugar del incidente.", "Attaches electronically to the event — audio, video and continuous tracking at the incident location.", "Conecta-se eletronicamente ao evento — áudio, vídeo e rastreamento contínuo no local do incidente.") },
    { Icon: Dog, t: pick("Unidades K9 y patrullaje", "K9 units & patrol", "Unidades K9 e patrulhamento"),
      d: pick("Personal especializado, unidades caninas y patrullaje coordinado con la sala de control.", "Specialized personnel, canine units and patrolling coordinated with the control room.", "Pessoal especializado, unidades caninas e patrulhamento coordenado com a sala de controle.") },
    { Icon: GraduationCap, t: pick("Capacitación continua", "Continuous training", "Capacitação contínua"),
      d: pick("Formación permanente en tácticas, protocolos y trato al cliente para cada guardia asignado.", "Ongoing training in tactics, protocols and client relations for every assigned guard.", "Formação permanente em táticas, protocolos e atendimento ao cliente para cada guarda designado.") },
  ];

  const KPIS = [
    { k: "360°", v: pick("Efectividad operativa", "Operational effectiveness", "Efetividade operacional") },
    { k: "24 / 7", v: pick("Monitoreo continuo", "Continuous monitoring", "Monitoramento contínuo") },
    { k: "+25", v: pick("Años protegiendo LATAM", "Years protecting LATAM", "Anos protegendo a América Latina") },
  ];

  return (
    <div className="min-h-screen text-white" data-testid="seguridad-privada-page">
      <NavBar />

      {/* HERO */}
      <section className="relative overflow-hidden h-[62vh] min-h-[440px] max-h-[720px] bg-[#06080D]">
        <CmsImage
          slotKey="seguridad.hero"
          defaultSrc="/seguridad-privada/cctv-guard.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-[center_30%] opacity-70"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#06080D] via-[#06080D]/70 to-[#06080D]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#06080D] via-transparent to-transparent" />
        <div className="absolute inset-0 grid-bg opacity-[0.08]" />

        <div className="relative h-full max-w-7xl mx-auto px-5 sm:px-6 md:px-10 flex items-end pb-10 md:pb-14 pt-32 sm:pt-36 md:pt-40">
          <div className="max-w-2xl">
            <Link to="/servicios" className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-300 transition mb-4" data-testid="back-to-services">
              <ArrowLeft className="w-4 h-4" />
              <span className="label-mono">{pick("SERVICIOS / ALL", "SERVICES / ALL", "SERVIÇOS / ALL")}</span>
            </Link>
          </div>
        </div>

        {/* Centered page title over the banner */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 flex items-center justify-center pointer-events-none px-4"
          data-testid="seguridad-banner-title"
        >
          <span
            className="font-display chrome-text text-4xl sm:text-6xl md:text-7xl lg:text-[6.5rem] tracking-[0.06em] leading-none text-center whitespace-nowrap"
            style={{ filter: 'drop-shadow(0 4px 24px rgba(0,0,0,0.85))' }}
          >
            {pick("SEGURIDAD PRIVADA", "PRIVATE SECURITY", "SEGURANÇA PRIVADA")}
          </span>
        </motion.h1>
        <div className="absolute bottom-0 inset-x-0 h-10 bg-gradient-to-b from-transparent to-[#06080D] pointer-events-none" />
      </section>

      {/* BLOCK 1 — Traje a la medida (image + text) */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="absolute top-1/3 -left-40 w-[560px] h-[560px] rounded-full bg-cyan-500/8 blur-[160px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: .7 }} className="lg:col-span-6 relative order-2 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden chrome-border shadow-[0_25px_60px_-15px_rgba(0,240,255,0.15)]">
                <CmsImage slotKey="seguridad.training" defaultSrc="/seguridad-privada/training.jpg" alt="" className="w-full h-full object-cover aspect-[4/3]" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#06080D]/35 via-transparent to-cyan-500/10 mix-blend-multiply" />
                <div className="absolute bottom-4 left-4 chrome-border rounded-xl px-3 py-2 flex items-center gap-2" style={{ background: 'rgba(6,8,13,0.85)', backdropFilter: 'blur(8px)' }}>
                  <GraduationCap className="w-3.5 h-3.5 text-cyan-300" />
                  <span className="label-mono !text-cyan-300 text-[10px]">
                    {pick("PERFIL · CAPACITACIÓN · HABILIDADES", "PROFILE · TRAINING · SKILLS", "PERFIL · CAPACITAÇÃO · HABILIDADES")}
                  </span>
                </div>
              </div>
              <div className="absolute -top-2 -left-2 w-6 h-6 border-t border-l border-cyan-400/40 pointer-events-none" />
              <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b border-r border-cyan-400/40 pointer-events-none" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: .7, delay: .1 }} className="lg:col-span-6 order-1 lg:order-2">
              <div className="label-mono mb-4">/ {pick("Traje a la medida", "Tailor-made", "Sob medida")}</div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl leading-[1.08] chrome-text">
                {pick("El guardia correcto para ", "The right guard for ", "O guarda certo para ")}
                <span className="text-cyan-300 italic">{pick("cada puesto.", "every post.", "cada posto.")}</span>
              </h2>
              <p className="mt-6 text-white/85 text-base sm:text-lg leading-relaxed">{pick(P1_ES, P1_EN, P1_PT)}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BLOCK 2 — Comunicación transnacional (text + image) */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="absolute top-1/2 -right-40 w-[560px] h-[560px] rounded-full bg-emerald-500/8 blur-[160px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: .7 }} className="lg:col-span-6">
              <div className="label-mono mb-4">/ {pick("Comunicación transnacional", "Transnational communication", "Comunicação transnacional")}</div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl leading-[1.08] chrome-text">
                {pick("Sin protocolos ", "Zero bureaucracy. ", "Sem protocolos ")}
                <span className="text-cyan-300 italic">{pick("que frenen.", "Full transparency.", "que atrasam.")}</span>
              </h2>
              <p className="mt-6 text-white/85 text-base sm:text-lg leading-relaxed">{pick(P2_ES, P2_EN, P2_PT)}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {[
                  pick("Guardia en campo", "Field guard", "Guarda em campo"),
                  pick("Supervisión", "Supervision", "Supervisão"),
                  pick("Alta dirección", "Top management", "Alta direção"),
                  pick("Cliente", "Client", "Cliente"),
                ].map((tag, i) => (
                  <span key={i} className="px-3 py-1 rounded-full border border-cyan-400/25 bg-cyan-400/5 text-cyan-200/90 text-xs font-tech tracking-wider">{tag}</span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: .7, delay: .1 }} className="lg:col-span-6 relative">
              <div className="relative rounded-2xl overflow-hidden chrome-border shadow-[0_25px_60px_-15px_rgba(0,240,255,0.15)]">
                <CmsImage slotKey="seguridad.radio" defaultSrc="/seguridad-privada/radio-guard.jpg" alt="" className="w-full h-full object-cover aspect-[4/3]" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tl from-[#06080D]/35 via-transparent to-emerald-500/10 mix-blend-multiply" />
                <div className="absolute bottom-4 right-4 chrome-border rounded-xl px-3 py-2 flex items-center gap-2" style={{ background: 'rgba(6,8,13,0.85)', backdropFilter: 'blur(8px)' }}>
                  <Radio className="w-3.5 h-3.5 text-emerald-300" />
                  <span className="label-mono !text-emerald-300 text-[10px]">
                    {pick("CANAL DIRECTO · TIEMPO REAL", "DIRECT CHANNEL · REAL-TIME", "CANAL DIRETO · TEMPO REAL")}
                  </span>
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 border-t border-r border-emerald-400/40 pointer-events-none" />
              <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b border-l border-emerald-400/40 pointer-events-none" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* BLOCK 3 — Monitoreo 360 (feature block with K9 image) */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="relative overflow-hidden rounded-3xl chrome-border">
            <CmsImage slotKey="seguridad.k9" defaultSrc="/seguridad-privada/k9-unit.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#06080D]/85 via-[#06080D]/70 to-[#06080D]/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#06080D] via-transparent to-transparent" />

            <div className="relative p-6 sm:p-10 md:p-14">
              <div className="grid lg:grid-cols-12 gap-8 items-start">
                <div className="lg:col-span-7">
                  <div className="label-mono !text-cyan-300 mb-4">/ {pick("Centro de monitoreo", "Monitoring center", "Central de monitoramento")}</div>
                  <h2 className="font-display text-3xl sm:text-4xl md:text-5xl leading-[1.08] chrome-text">
                    {pick("Efectividad ", "Effectiveness ", "Efetividade ")}
                    <span className="text-cyan-300 italic">360°.</span>
                  </h2>
                  <p className="mt-5 text-white/85 text-base sm:text-lg leading-relaxed max-w-2xl">{pick(P3_ES, P3_EN, P3_PT)}</p>
                </div>

                <div className="lg:col-span-5 grid grid-cols-3 gap-3">
                  {KPIS.map((kpi, i) => (
                    <div key={i} className="glass-card chrome-border p-4 text-center">
                      <div className="font-display text-2xl sm:text-3xl chrome-text leading-none">{kpi.k}</div>
                      <div className="label-mono !text-cyan-300 mt-2 text-[10px] leading-tight">{kpi.v}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 grid sm:grid-cols-3 gap-3">
                {[
                  { i: Eye,   t: pick("Audio en vivo", "Live audio", "Áudio ao vivo") },
                  { i: Radar, t: pick("Video continuo", "Continuous video", "Vídeo contínuo") },
                  { i: ShieldCheck, t: pick("Trackeo en tiempo real", "Real-time tracking", "Rastreamento em tempo real") },
                ].map(({ i: Ic, t }, idx) => (
                  <div key={idx} className="flex items-center gap-3 rounded-xl chrome-border px-4 py-3 bg-[#0B0F17]/60 backdrop-blur">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-cyan-400/15 border border-cyan-400/40">
                      <Ic className="w-4 h-4 text-cyan-300" />
                    </span>
                    <span className="font-tech text-sm text-white/90">{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCK 4 — Pillars grid */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="mb-6 max-w-2xl">
            <div className="label-mono mb-3">/ {pick("Servicios", "Services", "Serviços")}</div>
            <h2 className="font-display text-3xl md:text-4xl chrome-text leading-tight">
              {pick("Del guardia al centro de crisis, sin interrupciones.", "From the guard to the crisis room, seamlessly.", "Do guarda à sala de crises, sem interrupções.")}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PILLARS.map((s, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: .5, delay: i * 0.06 }}
                className="glass-card chrome-border p-6 group hover:border-cyan-400/50 transition-colors"
                data-testid={`seguridad-privada-pillar-${i}`}>
                <span className="w-10 h-10 rounded-lg flex items-center justify-center bg-cyan-400/15 border border-cyan-400/40 group-hover:bg-cyan-400/25 transition">
                  <s.Icon className="w-5 h-5 text-cyan-300" />
                </span>
                <h3 className="mt-4 font-tech text-base text-white leading-tight">{s.t}</h3>
                <p className="mt-2 text-[13px] text-white/70 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BLOCK 5 — Closer statement (multinational corp behind every guard) */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="relative overflow-hidden rounded-3xl chrome-border p-8 sm:p-10 md:p-14 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-emerald-500/10 pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/10 backdrop-blur mb-5">
                <Building2 className="w-3.5 h-3.5 text-cyan-300" />
                <span className="label-mono !text-cyan-300">
                  {pick("CORPORACIÓN MULTINACIONAL", "MULTINATIONAL CORPORATION", "CORPORAÇÃO MULTINACIONAL")}
                </span>
              </div>
              <h3 className="font-display text-2xl sm:text-3xl md:text-4xl chrome-text leading-[1.15] max-w-4xl mx-auto italic">
                “<CmsText
                  slotKey="seguridad.closer"
                  es={CLOSER_ES}
                  en={CLOSER_EN}
                  pt={CLOSER_PT}
                  lang={pick("es","en","pt")}
                />”
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="relative overflow-hidden rounded-3xl chrome-border p-8 sm:p-10 md:p-14">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-emerald-500/10 pointer-events-none" />
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div className="label-mono mb-3 !text-cyan-300">
                  {pick("SEGURIDAD A LA MEDIDA", "SECURITY MADE TO MEASURE", "SEGURANÇA SOB MEDIDA")}
                </div>
                <h3 className="font-display text-2xl sm:text-3xl md:text-4xl chrome-text leading-tight max-w-xl">
                  <CmsText
                    slotKey="seguridad.cta"
                    es="Hablemos del esquema que tu operación necesita."
                    en="Let's talk about the scheme your operation needs."
                    pt="Vamos falar sobre o esquema que sua operação precisa."
                    lang={pick("es","en","pt")}
                  />
                </h3>
              </div>
              <a
                {...waProps(content?.contact?.whatsapp
                  ? `https://wa.me/${String(content.contact.whatsapp).replace(/[^0-9]/g,'')}?text=${encodeURIComponent(pick(
                      'Hola, me interesa el servicio de Seguridad Privada',
                      'Hi, I am interested in the Private Security service',
                      'Olá, tenho interesse no serviço de Segurança Privada'
                    ))}`
                  : "#contact")}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400 text-black font-tech uppercase tracking-wider text-sm shadow-[0_0_28px_-4px_rgba(0,240,255,0.7)] hover:shadow-[0_0_36px_-4px_rgba(0,240,255,0.95)] transition self-start md:self-auto"
                data-testid="seguridad-privada-cta-whatsapp">
                {waLabel(pick, "Escribir por WhatsApp", "Message on WhatsApp", "Falar pelo WhatsApp")}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer content={content} />
      <FloatingWhatsApp phone={content?.contact?.whatsapp} />
    </div>
  );
}
